import React, { useCallback, useEffect, useMemo, useState } from 'react';
import researchService from '../../services/researchService';
import EarningsPdfViewer from './EarningsPdfViewer';

/** Flatten any object-valued metric keys: {custom_metrics: {k:v}} → {'custom_metrics.k': v} */
function flattenMetrics(metrics) {
  if (!metrics) return {};
  const out = {};
  Object.entries(metrics).forEach(([k, v]) => {
    // Skip corrupted legacy values (JS objects that were stringified before saving)
    if (typeof v === 'string' && v.includes('[object')) return;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      Object.entries(v).forEach(([sk, sv]) => { out[`${k}.${sk}`] = sv; });
    } else {
      out[k] = v;
    }
  });
  return out;
}

/**
 * Given two candidate values for the same metric, return the "better" one:
 *   - A full ISO date (YYYY-MM-DD) beats a bare year (YYYY)
 *   - A more-precise number (fewer trailing zeros) beats a rounded one
 * Falls back to newVal if no clear winner.
 */
function pickBetter(newVal, existingVal) {
  if (newVal == null) return existingVal;
  if (existingVal == null) return newVal;

  const sNew   = String(newVal).trim();
  const sExist = String(existingVal).trim();

  // Date preference: full ISO date > bare year
  const isFullDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const isBareYear = (s) => /^\d{4}$/.test(s);
  if (isFullDate(sNew)   && isBareYear(sExist)) return newVal;
  if (isFullDate(sExist) && isBareYear(sNew))   return existingVal;

  // Number precision: prefer value with fewer trailing zeros (less rounded)
  const nNew   = parseFloat(sNew);
  const nExist = parseFloat(sExist);
  if (!isNaN(nNew) && !isNaN(nExist) && nNew !== nExist) {
    const trailingZeros = (n) => {
      const abs = Math.round(Math.abs(n));
      if (abs === 0) return 0;
      const s = String(abs);
      return s.length - s.replace(/0+$/, '').length;
    };
    if (trailingZeros(nNew)   < trailingZeros(nExist)) return newVal;
    if (trailingZeros(nExist) < trailingZeros(nNew))   return existingVal;
  }

  return newVal; // default: new value wins
}

/**
 * For a period, detect metrics where the new/existing conflict is purely a
 * magnitude (scale) difference — ratio close to a power of 10 (10^n, n≥1).
 * Picks the value whose order-of-magnitude matches the consensus of stable
 * metrics in the same period (those WITHOUT a magnitude-scale conflict).
 *
 * Returns { metricKey: preferredValue } for magnitude-override cases only.
 */
function resolveMagnitudeConflicts(flatNew, flatExisting) {
  const MAG_THRESHOLD = 0.2; // within 0.2 log-units of a round power of 10

  // Non-financial integer/date fields that must never enter magnitude logic.
  const NON_MONETARY = new Set([
    'fiscal_year', 'year', 'fiscal_quarter', 'quarter',
    'period_end_date', 'reporting_period',
  ]);
  const NON_MONETARY_SUFFIX = /(_year|_quarter|_date|_period|_pct|_margin|_rate|_ratio|_percent)$/i;

  // Collect pairs where new and existing differ by a clean power of 10 (≥10x).
  const magConflicts = []; // { key, nv, ev, logRatio }

  const allKeys = new Set([...Object.keys(flatNew), ...Object.keys(flatExisting)]);
  allKeys.forEach((k) => {
    // Skip metadata / ratio / date keys — not monetary values.
    const bare = k.includes('.') ? k.slice(k.lastIndexOf('.') + 1) : k;
    if (NON_MONETARY.has(bare) || NON_MONETARY_SUFFIX.test(bare)) return;

    const nv = parseFloat(flatNew[k]);
    const ev = parseFloat(flatExisting[k]);

    // Both sides must be non-zero numbers for a meaningful scale comparison.
    if (isNaN(nv) || nv === 0 || isNaN(ev) || ev === 0) return;

    const logRatio   = Math.log10(Math.abs(nv / ev));
    const nearestInt = Math.round(logRatio);

    if (nearestInt !== 0 && Math.abs(logRatio - nearestInt) <= MAG_THRESHOLD) {
      magConflicts.push({ key: k, nv: flatNew[k], ev: flatExisting[k], logRatio });
    }
  });

  if (magConflicts.length === 0) return {};

  // Direction consensus: if the majority of conflicts have new > existing (logRatio > 0)
  // then the new extraction is the ones-based authoritative value — pick new for ALL.
  // This is reliable because magnitude-scale migrations are uniform: when old data was
  // stored in millions every metric will be off by the same factor in the same direction.
  const positiveCount = magConflicts.filter((c) => c.logRatio > 0).length;
  const preferNew = positiveCount >= magConflicts.length / 2;

  const overrides = {};
  magConflicts.forEach(({ key, nv, ev }) => {
    overrides[key] = preferNew ? nv : ev;
  });
  return overrides;
}

/** Re-nest dotted keys back to nested objects for the backend payload */
function unflattenMetrics(flat) {
  const out = {};
  Object.entries(flat).forEach(([k, v]) => {
    const dot = k.indexOf('.');
    if (dot !== -1) {
      const parent = k.slice(0, dot);
      const child = k.slice(dot + 1);
      if (!out[parent] || typeof out[parent] !== 'object') out[parent] = {};
      out[parent][child] = v;
    } else {
      out[k] = v;
    }
  });
  return out;
}

/**
 * Modal shown after extracting earnings from a new file.
 *
 * For each period the extractor found:
 *   - Shows fiscal year + quarter prominently
 *   - Side-by-side table: metric | existing value | new value
 *   - Conflicting values are highlighted; user picks which to keep (or edits a custom value)
 *
 * If there are custom metric keys that appear to be aliases of existing keys, a
 * key-remap section lets the user rename new keys to match existing ones (or keep as new).
 * An LLM suggestion can be fetched automatically.
 *
 * Props:
 *   preview      – response from researchService.previewEarningsUpload()
 *   companyId    – for the LLM remap call
 *   onConfirm(confirmedPeriods) – user approved; caller should call finalizeEarningsUpload
 *   onCancel()   – user dismissed without saving
 */
export default function EarningsReviewDialog({ preview, fileId, companyId, onConfirm, onCancel }) {
  // Per-period: { [periodEndDate]: { [metricKey]: chosen_value_string } }
  const [choices, setChoices] = useState({});

  // Key remap: { [new_key]: canonical_key_string }
  const [remap, setRemap] = useState({});
  const [remapExplanation, setRemapExplanation] = useState('');
  const [remapLoading, setRemapLoading] = useState(false);
  const [remapFetched, setRemapFetched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);      // presigned URL for the PDF viewer
  const [pdfSearchKey, setPdfSearchKey] = useState(null); // metric key to highlight
  const [pdfSearchText, setPdfSearchText] = useState(null); // verbatim source quote

  const openPdfForKey = useCallback(async (metricKey, sourceText) => {
    if (!fileId) return;
    // If same key clicked again, close
    if (pdfSearchKey === metricKey && pdfUrl) {
      setPdfSearchKey(null);
      setPdfSearchText(null);
      return;
    }
    setPdfSearchKey(metricKey);
    setPdfSearchText(sourceText || null);
    if (!pdfUrl) {
      setDocLoading(true);
      try {
        const { url } = await researchService.getFileDownloadUrl(fileId);
        setPdfUrl(url);
      } catch (err) {
        alert('Could not load document: ' + (err?.response?.data?.detail || err.message));
        setPdfSearchKey(null);
        setPdfSearchText(null);
      } finally {
        setDocLoading(false);
      }
    }
  }, [fileId, pdfUrl, pdfSearchKey]);

  const periods = preview?.periods || [];
  const keyDriftCandidates = preview?.key_drift_candidates || [];
  const hasKeyDrift = keyDriftCandidates.length > 0;

  // Build a flat lookup of all values across all periods so the remap table
  // can show sample values next to each key for easier comparison.
  const keyValueSamples = useMemo(() => {
    const newVals = {};
    const existingVals = {};
    periods.forEach((period) => {
      const flatNew = flattenMetrics(period.metrics);
      const flatExisting = flattenMetrics(period.existing_metrics);
      Object.entries(flatNew).forEach(([k, v]) => {
        if (v != null && !(k in newVals)) newVals[k] = v;
      });
      Object.entries(flatExisting).forEach(([k, v]) => {
        if (v != null && !(k in existingVals)) existingVals[k] = v;
      });
    });
    return { newVals, existingVals };
  }, [periods]);

  // Initialise choices: for each metric in each period, default to the NEW value
  useEffect(() => {
    const initial = {};
    periods.forEach((period) => {
      const ped = period.period_end_date;
      initial[ped] = {};
      const flatNew = flattenMetrics(period.metrics);
      const flatExisting = flattenMetrics(period.existing_metrics);
      // Magnitude pass first: detect conflicts that differ by a clean power of 10
      // and resolve them via consensus scale (or prefer new value if no stable anchors).
      // These pairs must NOT go through pickBetter — trailing-zero heuristics are
      // unreliable when the two values differ by a factor of 1000.
      const magOverrides = resolveMagnitudeConflicts(flatNew, flatExisting);

      // All new metric keys: for conflicts pick the more precise / complete value
      Object.entries(flatNew).forEach(([k, v]) => {
        if (k in magOverrides) {
          initial[ped][k] = magOverrides[k] == null ? '' : String(magOverrides[k]);
          return;
        }
        const existV = flatExisting[k];
        const sNew   = v     == null ? '' : String(v);
        const sExist = existV == null ? '' : String(existV);
        if (existV != null && sNew !== sExist) {
          const better = pickBetter(v, existV);
          initial[ped][k] = better == null ? '' : String(better);
        } else {
          initial[ped][k] = sNew;
        }
      });
      // Existing-only keys (not in new extraction) keep existing value
      Object.entries(flatExisting).forEach(([k, v]) => {
        if (!(k in flatNew)) {
          initial[ped][k] = v == null ? '' : String(v);
        }
      });
    });
    setChoices(initial);
  }, [preview]);

  // Initialise remap: new_key → new_key (identity = keep as-is)
  useEffect(() => {
    // Unit-scale tokens that should never be the canonical key name.
    // If the similar existing key contains one of these, default to keeping
    // the new key (no remap) rather than perpetuating a scaled key name.
    const SCALE_TOKENS = /_(millions?|thousands?|billions?|mm|bn|k)(_|$)/i;
    const init = {};
    keyDriftCandidates.forEach(({ new_key, similar_existing }) => {
      init[new_key] = SCALE_TOKENS.test(similar_existing) ? new_key : similar_existing;
    });
    setRemap(init);
  }, [keyDriftCandidates]);

  const fetchRemapSuggestion = useCallback(async () => {
    setRemapLoading(true);
    try {
      const result = await researchService.suggestKeyRemap(
        companyId,
        preview.all_existing_keys || [],
        preview.all_new_keys || [],
      );
      setRemap((prev) => {
        const SCALE_TOKENS = /_(millions?|thousands?|billions?|mm|bn|k)(_|$)/i;
        const next = { ...prev };
        Object.entries(result.remap || {}).forEach(([nk, ck]) => {
          // Only override drift candidates that we're showing in the UI.
          // Ignore AI suggestions that point to a scale-suffixed key.
          if (nk in next && !SCALE_TOKENS.test(ck)) next[nk] = ck;
        });
        return next;
      });
      setRemapExplanation(result.explanation || '');
    } catch (err) {
      setRemapExplanation(`LLM suggestion failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRemapLoading(false);
      setRemapFetched(true);
    }
  }, [companyId, preview]);

  const handleChoose = useCallback((ped, key, value) => {
    setChoices((prev) => ({
      ...prev,
      [ped]: { ...prev[ped], [key]: String(value) },
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    setSaving(true);
    try {
      // Apply key remap to each period's metrics
      const confirmedPeriods = periods.map((period) => {
        const ped = period.period_end_date;
        const rawChoices = choices[ped] || {};

        // Build remapped metrics (re-apply key remap, then unflatten dotted keys)
        const flatConfirmed = {};
        Object.entries(rawChoices).forEach(([k, v]) => {
          const canonicalKey = remap[k] !== undefined ? remap[k] : k;
          const parsed = parseFloat(v);
          flatConfirmed[canonicalKey] = isNaN(parsed) ? v : parsed;
        });
        const confirmed_metrics = unflattenMetrics(flatConfirmed);

        return {
          period_end_date: ped,
          fiscal_year: period.fiscal_year,
          fiscal_quarter: period.fiscal_quarter,
          currency: period.currency || 'USD',
          is_primary: period.is_primary,
          confirmed_metrics,
        };
      });

      await onConfirm(confirmedPeriods);
    } finally {
      setSaving(false);
    }
  }, [periods, choices, remap, onConfirm]);

  // All metric keys for a period (union of new + existing), with nested objects flattened
  const allKeysForPeriod = useCallback((period) => {
    const flatNew = flattenMetrics(period.metrics);
    const flatExisting = flattenMetrics(period.existing_metrics);
    const keys = new Set([
      ...Object.keys(flatNew),
      ...Object.keys(flatExisting),
    ]);
    return Array.from(keys).sort();
  }, []);

  if (!preview) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1060, overflowY: 'auto' }}
      >
        <div
          className={`modal-dialog modal-dialog-centered${pdfUrl ? '' : ' modal-xl modal-dialog-scrollable'}`}
          style={pdfUrl ? { maxWidth: '98vw', width: '98vw', margin: '1vh auto', height: '96vh', display: 'flex' } : {}}
          role="document"
        >
          <div className="modal-content" style={pdfUrl ? { flexDirection: 'row', height: '100%', overflow: 'hidden' } : {}}>
            {/* Review side — fixed width when PDF panel is open */}
            <div style={pdfUrl ? { flex: '0 0 420px', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '2px solid #444' } : {}}>
            {/* Header */}
            <div className="modal-header bg-dark text-white">
              <h5 className="modal-title">
                <i className="bi bi-clipboard-check me-2" />
                Review Extracted Earnings
              </h5>
              <div className="d-flex align-items-center gap-2 ms-auto">
                {fileId && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={() => {
                      if (pdfUrl) { setPdfUrl(null); setPdfSearchKey(null); setPdfSearchText(null); }
                      else openPdfForKey(null);
                    }}
                    disabled={docLoading || saving}
                    title={pdfUrl ? 'Close PDF viewer' : 'Open source document'}
                  >
                    {docLoading
                      ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</>
                      : pdfUrl
                      ? <><i className="bi bi-layout-split me-1" />Close PDF</>
                      : <><i className="bi bi-file-earmark-text me-1" />View Document</>}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={onCancel}
                  disabled={saving}
                  aria-label="Close"
                  style={pdfUrl ? { display: 'none' } : undefined}
                />
              </div>
            </div>

            <div className="modal-body p-0" style={{ flex: 1, minHeight: 0, overflowY: 'auto', maxHeight: pdfUrl ? undefined : '70vh' }}>
              {/* Instructions */}
              {!pdfUrl && (
              <div className="alert alert-info rounded-0 mb-0 small py-2 px-3">
                Review the values extracted from this file. Conflicts (existing value differs from new) are
                highlighted. Click <strong>Existing</strong> or <strong>New</strong> to select a value,
                or type a custom value directly.
              </div>
              )}

              {/* Key drift / remap section */}
              {hasKeyDrift && (
                <div className="border-bottom p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-shuffle text-warning" />
                    <span className="fw-semibold small">
                      Potential metric key aliases detected — review and confirm remapping
                    </span>
                    {!remapFetched && (
                      <button
                        className="btn btn-sm btn-outline-primary ms-auto"
                        onClick={fetchRemapSuggestion}
                        disabled={remapLoading}
                      >
                        {remapLoading ? (
                          <><span className="spinner-border spinner-border-sm me-1" />Getting LLM suggestion…</>
                        ) : (
                          <><i className="bi bi-robot me-1" />Suggest with AI</>
                        )}
                      </button>
                    )}
                  </div>
                  {remapExplanation && (
                    <div className="small text-muted mb-2 fst-italic">{remapExplanation}</div>
                  )}
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                    <thead className="table-secondary">
                      <tr>
                        <th>New key (from file)</th>
                        <th className="text-end">New value</th>
                        <th>Similar existing key</th>
                        <th className="text-end">Existing value</th>
                        <th>Map to (canonical name)</th>
                        <th style={{ width: '1%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyDriftCandidates.map(({ new_key, similar_existing }) => {
                        const newVal = keyValueSamples.newVals[new_key];
                        const existingVal = keyValueSamples.existingVals[similar_existing];
                        const fmtVal = (v) => {
                          if (v == null) return <span className="text-muted">—</span>;
                          const n = Number(v);
                          if (!isNaN(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
                          return String(v);
                        };
                        const currentVal = remap[new_key] ?? new_key;
                        const isRejected = currentVal === new_key;
                        return (
                          <tr key={new_key} className={isRejected ? 'table-light text-muted' : ''}>
                            <td><code className={isRejected ? 'text-muted' : ''}>{new_key}</code></td>
                            <td className="text-end font-monospace">{fmtVal(newVal)}</td>
                            <td><code className={isRejected ? 'text-decoration-line-through text-muted' : ''}>{similar_existing}</code></td>
                            <td className="text-end font-monospace">{fmtVal(existingVal)}</td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={currentVal}
                                disabled={isRejected}
                                onChange={(e) =>
                                  setRemap((prev) => ({ ...prev, [new_key]: e.target.value }))
                                }
                              />
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {isRejected ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm py-0"
                                  title="Restore suggested remap"
                                  onClick={() => setRemap((prev) => ({ ...prev, [new_key]: similar_existing }))}
                                >
                                  ↩
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm py-0"
                                  title="Keep as new key (reject remap)"
                                  onClick={() => setRemap((prev) => ({ ...prev, [new_key]: new_key }))}
                                >
                                  ✕
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* One section per extracted period */}
              {periods.map((period) => {
                const ped = period.period_end_date;
                const periodChoices = choices[ped] || {};
                const flatNew = flattenMetrics(period.metrics);
                const flatExisting = flattenMetrics(period.existing_metrics);
                const flatPrev = flattenMetrics(period.prev_quarter_metrics);
                const prevQuarterPed = period.prev_quarter_ped || null;
                const allKeys = allKeysForPeriod(period);
                const standardKeys = allKeys.filter(k => !k.startsWith('custom_metrics.'));
                const customKeys = allKeys.filter(k => k.startsWith('custom_metrics.'));
                const citations = period.citations || {}; // {metricKey: "verbatim source quote"}
                const conflictCount = allKeys.filter(
                  (k) => flatExisting[k] != null && flatNew[k] != null &&
                         String(flatExisting[k]) !== String(flatNew[k])
                ).length;
                const largeDiffCount = allKeys.filter((k) => {
                  if (flatExisting[k] == null || flatNew[k] == null) return false;
                  if (String(flatExisting[k]) === String(flatNew[k])) return false;
                  const a = parseFloat(flatExisting[k]);
                  const b = parseFloat(flatNew[k]);
                  if (isNaN(a) || isNaN(b)) return false;
                  const denom = Math.max(Math.abs(a), Math.abs(b));
                  if (denom === 0) return a !== b;
                  return Math.abs(a - b) / denom > 0.20;
                }).length;

                return (
                  <div key={ped} className="border-bottom p-3">
                    {/* Period header */}
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div>
                        <span className="badge bg-primary fs-6 me-2">
                          {period.fiscal_year
                            ? `FY ${period.fiscal_year}`
                            : '—'}
                        </span>
                        <span className="badge bg-secondary fs-6 me-2">
                          {period.fiscal_quarter
                            ? `Q${period.fiscal_quarter}`
                            : '—'}
                        </span>
                        {period.quarter_label &&
                          period.quarter_label !== `Q${period.fiscal_quarter}` && (
                          <span className="badge bg-dark fs-6">
                            {period.quarter_label}
                          </span>
                        )}
                      </div>
                      <span className="small text-muted">Period end: {ped}</span>
                      {period.is_primary && (
                        <span className="badge bg-success ms-auto">Primary period</span>
                      )}
                      {!period.has_existing && (
                        <span className="badge bg-info ms-auto">New — no existing record</span>
                      )}
                      {period.has_existing && conflictCount === 0 && (
                        <span className="badge bg-success ms-auto">No conflicts</span>
                      )}
                      {period.has_existing && conflictCount > 0 && largeDiffCount === 0 && (
                        <span className="badge bg-warning text-dark ms-auto">
                          {conflictCount} conflict{conflictCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {period.has_existing && largeDiffCount > 0 && (
                        <span className="badge bg-danger ms-auto">
                          {largeDiffCount} large diff{largeDiffCount > 1 ? 's' : ''} (&gt;20%)
                          {conflictCount > largeDiffCount && ` + ${conflictCount - largeDiffCount} other`}
                        </span>
                      )}
                    </div>

                    {/* Metrics comparison table */}
                    <div>
                      <table
                        className="table table-sm table-bordered mb-0"
                        style={{ fontSize: '0.78rem' }}
                      >
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th style={{ width: '24%' }}>Metric</th>
                            <th style={{ width: '17%' }} title={prevQuarterPed ? `Period: ${prevQuarterPed}` : 'No prior quarter in database'}>
                              Past Quarter{prevQuarterPed && <span className="fw-normal opacity-75" style={{ fontSize: '0.65rem' }}> {prevQuarterPed}</span>}
                            </th>
                            <th style={{ width: '17%' }}>Existing</th>
                            <th style={{ width: '19%' }}>New (extracted)</th>
                            <th style={{ width: '23%' }}>Selected value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {standardKeys.map((k) => {
                            const existingVal = flatExisting[k];
                            const newVal = flatNew[k];
                            const prevVal = flatPrev[k];
                            // Conflict: both sides have a value and they differ
                            const isConflict =
                              existingVal != null && newVal != null &&
                              String(existingVal) !== String(newVal);
                            const isNewOnly = existingVal == null && newVal != null;
                            const isExistingOnly = newVal == null && existingVal != null;
                            const chosen = periodChoices[k] != null && periodChoices[k] !== ''
                              ? periodChoices[k]
                              : (newVal != null ? String(newVal) : '');

                            // Large diff: numeric conflict where values differ by >20%
                            const isLargeDiff = isConflict && (() => {
                              const a = parseFloat(existingVal);
                              const b = parseFloat(newVal);
                              if (isNaN(a) || isNaN(b)) return false;
                              const denom = Math.max(Math.abs(a), Math.abs(b));
                              if (denom === 0) return a !== b;
                              return Math.abs(a - b) / denom > 0.20;
                            })();

                            // Large diff vs previous quarter (reference only)
                            const prevDiffPct = (() => {
                              if (prevVal == null || newVal == null) return null;
                              const a = parseFloat(prevVal);
                              const b = parseFloat(newVal);
                              if (isNaN(a) || isNaN(b)) return null;
                              const denom = Math.max(Math.abs(a), Math.abs(b));
                              if (denom === 0) return a !== b ? 100 : 0;
                              return Math.round(Math.abs(a - b) / denom * 100);
                            })();
                            const isPrevLargeDiff = prevDiffPct != null && prevDiffPct > 20;
                            const prevBadgeBg = isPrevLargeDiff
                              ? prevDiffPct < 40 ? '#fd7e14'
                              : prevDiffPct < 65 ? '#d95f00'
                              : '#dc3545'
                              : null;
                            const prevCellBg = isPrevLargeDiff
                              ? prevDiffPct < 40 ? '#fff3cd'
                              : prevDiffPct < 65 ? '#fde8d8'
                              : '#f8d7da'
                              : null;

                            const rowClass = isLargeDiff
                              ? 'table-danger'
                              : isConflict
                              ? 'table-warning'
                              : isNewOnly
                              ? 'table-success'
                              : '';

                            const fmt = (v) =>
                              v == null
                                ? <span className="text-muted fst-italic">—</span>
                                : typeof v === 'number'
                                ? v.toLocaleString(undefined, { maximumFractionDigits: 4 })
                                : String(v);

                            return (
                              <tr key={k} className={rowClass}>
                                <td className="fw-semibold text-break">
                                  <span
                                    role={fileId ? 'button' : undefined}
                                    title={citations[k] ? `Source: "${citations[k]}"` : fileId ? 'Click to highlight in document' : undefined}
                                    style={fileId ? { cursor: 'pointer' } : {}}
                                    onClick={fileId ? () => openPdfForKey(k, citations[k]) : undefined}
                                  >
                                    {fileId && (
                                      <i className="bi bi-search me-1" style={{ color: pdfSearchKey === k ? '#ffe066' : '#000', fontSize: '0.7rem' }} />
                                    )}
                                    {k}
                                  </span>
                                  {isLargeDiff && (
                                    <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }} title="Values differ by more than 20%">
                                      ⚠ &gt;20% diff
                                    </span>
                                  )}
                                  {isNewOnly && (
                                    <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>
                                      new
                                    </span>
                                  )}
                                  {isExistingOnly && (
                                    <span className="badge bg-secondary ms-1" style={{ fontSize: '0.65rem' }}>
                                      existing only
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={`text-end${isPrevLargeDiff ? ' fw-bold' : ''}`}
                                  style={isPrevLargeDiff ? { background: prevCellBg } : {}}
                                  title={isPrevLargeDiff ? `New value differs from prior quarter by ${prevDiffPct}%` : undefined}
                                >
                                  {fmt(prevVal)}
                                  {isPrevLargeDiff && (
                                    <span style={{ fontSize: '0.6rem', background: prevBadgeBg, color: '#fff', padding: '1px 5px', borderRadius: '0.25rem', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: '4px' }}>
                                      ⚠ {prevDiffPct}%
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {period.has_existing ? (
                                    <span
                                      className={`d-block text-end${isConflict ? ' fw-bold text-danger' : ''}`}
                                      role={isConflict ? 'button' : undefined}
                                      style={isConflict ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}
                                      title={isConflict ? 'Click to use existing value' : undefined}
                                      onClick={
                                        isConflict && existingVal != null
                                          ? () => handleChoose(ped, k, existingVal)
                                          : undefined
                                      }
                                    >
                                      {fmt(existingVal)}
                                    </span>
                                  ) : (
                                    <span className="text-muted fst-italic">—</span>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`d-block text-end${isConflict ? ' fw-bold text-primary' : ''}`}
                                    role={isConflict ? 'button' : undefined}
                                    style={isConflict ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}
                                    title={isConflict ? 'Click to use new value' : undefined}
                                    onClick={
                                      isConflict && newVal != null
                                        ? () => handleChoose(ped, k, newVal)
                                        : undefined
                                    }
                                  >
                                    {fmt(newVal)}
                                  </span>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className={`form-control form-control-sm text-end${isLargeDiff ? ' border-danger' : isConflict ? ' border-warning' : ''}`}
                                    value={chosen}
                                    onChange={(e) => handleChoose(ped, k, e.target.value)}
                                    style={{ fontSize: '0.78rem' }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {customKeys.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="fw-semibold small" style={{ color: '#6f42c1' }}>
                            <i className="bi bi-stars me-1" />Custom Metrics
                          </span>
                          <span className="badge" style={{ background: '#6f42c1', color: 'white' }}>
                            {customKeys.length}
                          </span>
                        </div>
                        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                          <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.78rem' }}>
                            <thead className="sticky-top" style={{ background: '#6f42c1', color: 'white' }}>
                              <tr>
                                <th style={{ width: '24%' }}>Metric</th>
                                <th style={{ width: '17%' }} title={prevQuarterPed ? `Period: ${prevQuarterPed}` : 'No prior quarter in database'}>
                                  Past Quarter{prevQuarterPed && <span className="fw-normal opacity-75 ms-1" style={{ fontSize: '0.7rem' }}>{prevQuarterPed}</span>}
                                </th>
                                <th style={{ width: '17%' }}>Existing</th>
                                <th style={{ width: '19%' }}>New (extracted)</th>
                                <th style={{ width: '23%' }}>Selected value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customKeys.map((k) => {
                                const existingVal = flatExisting[k];
                                const newVal = flatNew[k];
                                const prevVal = flatPrev[k];
                                const isConflict =
                                  existingVal != null && newVal != null &&
                                  String(existingVal) !== String(newVal);
                                const isNewOnly = existingVal == null && newVal != null;
                                const isExistingOnly = newVal == null && existingVal != null;
                                const chosen = periodChoices[k] != null && periodChoices[k] !== ''
                                  ? periodChoices[k]
                                  : (newVal != null ? String(newVal) : '');
                                const isLargeDiff = isConflict && (() => {
                                  const a = parseFloat(existingVal);
                                  const b = parseFloat(newVal);
                                  if (isNaN(a) || isNaN(b)) return false;
                                  const denom = Math.max(Math.abs(a), Math.abs(b));
                                  if (denom === 0) return a !== b;
                                  return Math.abs(a - b) / denom > 0.20;
                                })();
                                const prevDiffPct = (() => {
                                  if (prevVal == null || newVal == null) return null;
                                  const a = parseFloat(prevVal);
                                  const b = parseFloat(newVal);
                                  if (isNaN(a) || isNaN(b)) return null;
                                  const denom = Math.max(Math.abs(a), Math.abs(b));
                                  if (denom === 0) return a !== b ? 100 : 0;
                                  return Math.round(Math.abs(a - b) / denom * 100);
                                })();
                                const isPrevLargeDiff = prevDiffPct != null && prevDiffPct > 20;
                                const prevBadgeBg = isPrevLargeDiff
                                  ? prevDiffPct < 40 ? '#fd7e14'
                                  : prevDiffPct < 65 ? '#d95f00'
                                  : '#dc3545'
                                  : null;
                                const prevCellBg = isPrevLargeDiff
                                  ? prevDiffPct < 40 ? '#fff3cd'
                                  : prevDiffPct < 65 ? '#fde8d8'
                                  : '#f8d7da'
                                  : null;
                                const rowClass = isLargeDiff
                                  ? 'table-danger'
                                  : isConflict
                                  ? 'table-warning'
                                  : isNewOnly
                                  ? 'table-success'
                                  : '';
                                const displayKey = k.replace(/^custom_metrics\./, '');
                                const fmt = (v) =>
                                  v == null
                                    ? <span className="text-muted fst-italic">—</span>
                                    : typeof v === 'number'
                                    ? v.toLocaleString(undefined, { maximumFractionDigits: 4 })
                                    : String(v);
                                return (
                                  <tr key={k} className={rowClass}>
                                    <td className="fw-semibold text-break">
                                      <span
                                        role={fileId ? 'button' : undefined}
                                        title={citations[k] ? `Source: "${citations[k]}"` : fileId ? 'Click to highlight in document' : undefined}
                                        style={fileId ? { cursor: 'pointer' } : {}}
                                        onClick={fileId ? () => openPdfForKey(k, citations[k]) : undefined}
                                      >
                                        {fileId && (
                                          <i className="bi bi-search me-1" style={{ color: pdfSearchKey === k ? '#ffe066' : '#000', fontSize: '0.7rem' }} />
                                        )}
                                        {displayKey}
                                      </span>
                                      {isLargeDiff && (
                                        <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }} title="Values differ by more than 20%">
                                          ⚠ &gt;20% diff
                                        </span>
                                      )}
                                      {isNewOnly && (
                                        <span className="badge ms-1" style={{ fontSize: '0.65rem', background: '#6f42c1', color: 'white' }}>new</span>
                                      )}
                                      {isExistingOnly && (
                                        <span className="badge bg-secondary ms-1" style={{ fontSize: '0.65rem' }}>existing only</span>
                                      )}
                                    </td>
                                    <td
                                      className={`text-end${isPrevLargeDiff ? ' fw-bold' : ''}`}
                                      style={isPrevLargeDiff ? { background: prevCellBg } : {}}
                                      title={isPrevLargeDiff ? `New value differs from prior quarter by ${prevDiffPct}%` : undefined}
                                    >
                                      {fmt(prevVal)}
                                      {isPrevLargeDiff && (
                                        <span style={{ fontSize: '0.6rem', background: prevBadgeBg, color: '#fff', padding: '1px 5px', borderRadius: '0.25rem', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: '4px' }}>
                                          ⚠ {prevDiffPct}%
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      {period.has_existing ? (
                                        <span
                                          className={`d-block text-end${isConflict ? ' fw-bold text-danger' : ''}`}
                                          role={isConflict ? 'button' : undefined}
                                          style={isConflict ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}
                                          title={isConflict ? 'Click to use existing value' : undefined}
                                          onClick={isConflict && existingVal != null ? () => handleChoose(ped, k, existingVal) : undefined}
                                        >
                                          {fmt(existingVal)}
                                        </span>
                                      ) : (
                                        <span className="text-muted fst-italic">—</span>
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`d-block text-end${isConflict ? ' fw-bold text-primary' : ''}`}
                                        role={isConflict ? 'button' : undefined}
                                        style={isConflict ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}
                                        title={isConflict ? 'Click to use new value' : undefined}
                                        onClick={isConflict && newVal != null ? () => handleChoose(ped, k, newVal) : undefined}
                                      >
                                        {fmt(newVal)}
                                      </span>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className={`form-control form-control-sm text-end${isLargeDiff ? ' border-danger' : isConflict ? ' border-warning' : ''}`}
                                        value={chosen}
                                        onChange={(e) => handleChoose(ped, k, e.target.value)}
                                        style={{ fontSize: '0.78rem' }}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {periods.length === 0 && (
                <div className="text-muted small text-center py-4">
                  No periods were extracted from this file.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                Cancel — don't save
              </button>
              <button
                className="btn btn-success"
                onClick={handleConfirm}
                disabled={saving || periods.length === 0}
              >
                {saving ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                ) : (
                  <><i className="bi bi-check2-circle me-1" />Confirm &amp; Save</>
                )}
              </button>
            </div>
            </div>{/* end review side wrapper */}

            {/* PDF viewer side panel */}
            {pdfUrl && (
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <EarningsPdfViewer
                  url={pdfUrl}
                  searchKey={pdfSearchKey}
                  searchText={pdfSearchText}
                  onClose={() => { setPdfUrl(null); setPdfSearchKey(null); setPdfSearchText(null); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
